import { ROUTES } from '../routes'
import type { CheckoutOutcome } from './outcome'

/**
 * A purchase the buyer is following on the confirmation page
 * (`/_/checkout/payment/{purchaseId}`): the PIX or boleto to pay, and what to show once
 * it is paid. Kept in localStorage, because the API's purchase read has no PIX
 * code and a phone that leaves the tab to open the bank app often comes back
 * to a reloaded page. Nothing here is a secret the buyer does not already see.
 */
export interface PendingCheckout {
  purchaseId: string
  outcome: CheckoutOutcome
  /** "Growth · Mensal": the plan and the option. */
  lineLabel: string
  productTitle: string
  isSubscription: boolean
  /** The plan's benefits, shown as what the payment unlocks. */
  benefits?: string[]
  redirectUrl?: string | null
  thankYouMessage?: string | null
  /** Where "generate a new PIX" goes: the plan link the purchase started from. */
  retryPath: string
  createdAt: number
}

/* `/pagamento/` and `/payment/` before; old links are redirected (`routes.js`). */
export const PAYMENT_PATH_PREFIX = ROUTES.checkoutPayment

export const isPaymentPath = (path: string) => path.startsWith(PAYMENT_PATH_PREFIX)

export const paymentPath = (purchaseId: string) => `${PAYMENT_PATH_PREFIX}${encodeURIComponent(purchaseId)}`

const KEY = (purchaseId: string) => `memberfy:checkout:${purchaseId}`
const DAY_MS = 24 * 60 * 60 * 1000

export const savePendingCheckout = (pending: PendingCheckout) => {
  try {
    // Old records go: a day is far longer than any PIX or boleto shown here.
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i)
      if (!key?.startsWith('memberfy:checkout:')) continue
      const old = JSON.parse(localStorage.getItem(key) || 'null') as PendingCheckout | null
      if (!old || Date.now() - old.createdAt > DAY_MS) localStorage.removeItem(key)
    }
    localStorage.setItem(KEY(pending.purchaseId), JSON.stringify(pending))
  } catch {
    /* Private mode or full storage: the page still follows the purchase by id. */
  }
}

export const readPendingCheckout = (purchaseId: string): PendingCheckout | null => {
  try {
    return JSON.parse(localStorage.getItem(KEY(purchaseId)) || 'null')
  } catch {
    return null
  }
}
